import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BellOutlined, RightOutlined } from "@ant-design/icons";
import { Badge, Empty, Popover, Spin, Tag } from "antd";
import { Button } from "@/design-system";
import nodeAPI from "../../service/ApiServiceNode";
import logger from "../../utils/logger";
import { colorForCategory, formatCategoryLabel } from "../Notification/category-format";
import "./DashboardNotificationBell.scss";

export const DASHBOARD_NOTIFICATION_LIMIT = 5;
export const NOTIFICATION_COUNT_CHANNEL = "notification-count-sync";

const INTERNAL_NOTIFICATION_HOSTS = new Set([
  "hr.mediatropy.com",
  "hrp.mediatropy.com",
]);

const parseCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
};

const parseMessageDocument = (message) => {
  if (typeof DOMParser === "undefined") return null;
  return new DOMParser().parseFromString(String(message || ""), "text/html");
};

export const getNotificationPlainText = (message) => {
  const parsed = parseMessageDocument(message);
  if (!parsed) return String(message || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  parsed.querySelectorAll("script, style").forEach((node) => node.remove());
  return String(parsed.body?.textContent || "").replace(/\s+/g, " ").trim();
};

export const getNotificationHref = (notification) => {
  const directHref = String(notification?.linkUrl || "").trim();
  if (directHref) return directHref;

  const parsed = parseMessageDocument(notification?.message);
  return String(parsed?.querySelector("a[href]")?.getAttribute("href") || "").trim() || null;
};

export const formatRelativeNotificationTime = (dateString, now = Date.now()) => {
  const timestamp = new Date(dateString).getTime();
  if (!Number.isFinite(timestamp)) return "Recently";

  const elapsedMs = Math.max(0, now - timestamp);
  const minutes = Math.floor(elapsedMs / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
  }).format(new Date(timestamp));
};

const DashboardNotificationBell = ({ onNavigate }) => {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const countChannelRef = useRef(null);

  const applyUnreadCount = useCallback((count) => {
    setUnreadCount(parseCount(count));
  }, []);

  const broadcastUnreadCount = useCallback((count) => {
    const nextCount = parseCount(count);
    applyUnreadCount(nextCount);

    try {
      countChannelRef.current?.postMessage({ count: nextCount });
    } catch (_error) {
      // Count is already updated in this component; cross-tab sync is best effort.
    }
  }, [applyUnreadCount]);

  const fetchUnreadCount = useCallback(async () => {
    const response = await nodeAPI.get("/notification/count");
    const count = parseCount(response?.data?.data?.count);
    applyUnreadCount(count);
    return count;
  }, [applyUnreadCount]);

  const loadDropdown = useCallback(async () => {
    setLoading(true);
    setLoadError(false);

    const [countResult, notificationResult] = await Promise.allSettled([
      nodeAPI.get("/notification/count"),
      nodeAPI.get("/notification/recent", {
        params: { limit: DASHBOARD_NOTIFICATION_LIMIT },
      }),
    ]);

    if (countResult.status === "fulfilled") {
      applyUnreadCount(countResult.value?.data?.data?.count);
    } else {
      logger.error("Failed to refresh dashboard notification count:", countResult.reason);
    }

    if (notificationResult.status === "fulfilled") {
      setNotifications(notificationResult.value?.data?.data?.notifications || []);
    } else {
      logger.error("Failed to load dashboard notifications:", notificationResult.reason);
      setLoadError(true);
    }

    setLoading(false);
  }, [applyUnreadCount]);

  useEffect(() => {
    let cancelled = false;

    fetchUnreadCount().catch((error) => {
      if (!cancelled) logger.error("Failed to load dashboard notification count:", error);
    });

    try {
      if (typeof BroadcastChannel !== "undefined") {
        const channel = new BroadcastChannel(NOTIFICATION_COUNT_CHANNEL);
        channel.onmessage = (event) => {
          if (!cancelled && typeof event.data?.count === "number") {
            applyUnreadCount(event.data.count);
          }
        };
        countChannelRef.current = channel;
      }
    } catch (_error) {
      countChannelRef.current = null;
    }

    return () => {
      cancelled = true;
      try {
        countChannelRef.current?.close();
      } catch (_error) {
        // Ignore cleanup failures in browsers without full BroadcastChannel support.
      }
      countChannelRef.current = null;
    };
  }, [applyUnreadCount, fetchUnreadCount]);

  const handleOpenChange = useCallback((nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen) loadDropdown();
  }, [loadDropdown]);

  const navigateToNotificationTarget = useCallback((notification) => {
    const href = getNotificationHref(notification);

    if (!href) {
      onNavigate?.("/notification");
      return;
    }

    if (href.startsWith("/")) {
      onNavigate?.(href);
      return;
    }

    if (!/^https?:\/\//i.test(href)) {
      onNavigate?.("/notification");
      return;
    }

    try {
      const targetUrl = new URL(href);
      if (INTERNAL_NOTIFICATION_HOSTS.has(targetUrl.hostname)) {
        onNavigate?.(`${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`);
      } else {
        window.open(href, "_blank", "noopener,noreferrer");
      }
    } catch (_error) {
      onNavigate?.("/notification");
    }
  }, [onNavigate]);

  const handleNotificationClick = useCallback(async (notification) => {
    if (markingId !== null) return;

    setMarkingId(notification.id);
    try {
      await nodeAPI.post(`/notification/read/${notification.id}`);
      setNotifications((current) => current.filter((item) => item.id !== notification.id));
      broadcastUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      logger.error("Failed to mark dashboard notification as read:", error);
    } finally {
      setMarkingId(null);
      setOpen(false);
      navigateToNotificationTarget(notification);
    }
  }, [broadcastUnreadCount, markingId, navigateToNotificationTarget, unreadCount]);

  const unreadSummary = unreadCount === 1
    ? "1 unread notification"
    : `${unreadCount} unread notifications`;

  const dropdownContent = useMemo(() => (
    <section
      className="dashboard-notification-dropdown"
      role="dialog"
      aria-label="Recent notifications"
    >
      <header className="dashboard-notification-dropdown__header">
        <div>
          <span className="dashboard-notification-dropdown__eyebrow">Inbox</span>
          <h2>Notifications</h2>
        </div>
        <span className="dashboard-notification-dropdown__count" aria-live="polite">
          {unreadSummary}
        </span>
      </header>

      <div className="dashboard-notification-dropdown__body">
        {loading ? (
          <div className="dashboard-notification-dropdown__state" aria-label="Loading notifications">
            <Spin />
            <span>Loading notifications...</span>
          </div>
        ) : loadError ? (
          <div className="dashboard-notification-dropdown__state" role="alert">
            <strong>Notifications could not be loaded.</strong>
            <Button
              type="button"
              variant="secondary"
              size="small"
              uppercase={false}
              onClick={loadDropdown}
            >
              Try again
            </Button>
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="You're all caught up."
            className="dashboard-notification-dropdown__empty"
          />
        ) : (
          <div className="dashboard-notification-dropdown__list" role="list">
            {notifications.map((notification) => {
              const message = getNotificationPlainText(notification.message) || "Open notification";
              const relativeTime = formatRelativeNotificationTime(notification.createdAt);

              return (
                <div role="listitem" key={notification.id}>
                  <button
                    type="button"
                    className="dashboard-notification-dropdown__item"
                    onClick={() => handleNotificationClick(notification)}
                    disabled={markingId === notification.id}
                  >
                    <span className="dashboard-notification-dropdown__dot" aria-hidden="true" />
                    <span className="dashboard-notification-dropdown__copy">
                      <span className="dashboard-notification-dropdown__meta">
                        {notification.category ? (
                          <Tag color={colorForCategory(notification.category)}>
                            {formatCategoryLabel(notification.category)}
                          </Tag>
                        ) : (
                          <span>Update</span>
                        )}
                        <time dateTime={notification.createdAt}>{relativeTime}</time>
                      </span>
                      <span className="dashboard-notification-dropdown__message">{message}</span>
                    </span>
                    <RightOutlined className="dashboard-notification-dropdown__arrow" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <footer className="dashboard-notification-dropdown__footer">
        <Button
          type="button"
          aria-label="View all notifications"
          variant="secondary"
          size="small"
          uppercase={false}
          icon={<RightOutlined />}
          onClick={() => {
            setOpen(false);
            onNavigate?.("/notification");
          }}
        >
          View all notifications
        </Button>
      </footer>
    </section>
  ), [
    handleNotificationClick,
    loadDropdown,
    loadError,
    loading,
    markingId,
    notifications,
    onNavigate,
    unreadSummary,
  ]);

  return (
    <Popover
      content={dropdownContent}
      trigger="click"
      placement="bottomRight"
      open={open}
      onOpenChange={handleOpenChange}
      arrow={false}
      destroyOnHidden
      classNames={{ root: "dashboard-notification-popover" }}
    >
      <Badge
        count={unreadCount}
        overflowCount={unreadCount}
        size="small"
        offset={[-2, 4]}
        className="staff-dashboard-notification-badge"
      >
        <Button
          type="button"
          variant="secondary"
          size="small"
          uppercase={false}
          className="staff-dashboard-header-shortcut staff-dashboard-notification-button"
          icon={<BellOutlined />}
          aria-label={`Notifications, ${unreadSummary}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          title="Notifications"
        />
      </Badge>
    </Popover>
  );
};

export default DashboardNotificationBell;
